import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { consumePlpScrollTarget } from '@theme/plp-scroll-target';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLButtonElement} [loadMoreButton] - The load more button.
 * @property {HTMLElement} [productCountCurrent] - The element displaying the current visible product count.
 * @property {HTMLElement} [productCountTotal] - The element displaying the total product count.
 * @property {HTMLElement} [productCountProgress] - The progress bar wrapper element.
 * @property {HTMLElement} [productCountProgressBar] - The progress bar fill element.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {Set<number>} */
  #renderedPages = new Set();

  /** @type {boolean} */
  #isRenderingPrevious = false;

  /** @type {boolean} */
  #isRenderingNext = false;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {boolean} */
  #viewMorePreviousIntersecting = false;

  /** @type {boolean} */
  #hasRecordedViewMorePreviousState = false;

  /** @type {boolean} */
  #isBackfilling = false;

  /** @type {(event: Event) => void} */
  #handlePageShow = (event) => {
    // BFCache restore can re-trigger observers while DOM already contains loaded pages.
    // Rebuild rendered page state to avoid loading the same page twice.
    // @ts-ignore - persisted exists on pageshow events in browsers.
    if (event?.persisted) {
      this.infinityScrollObserver?.disconnect();
      this.#syncRenderedPagesFromDom();
      this.#updateProductCount();
      this.#restoreScrollTarget();
      this.#observeViewMore();
    }
  };

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.loadMoreButton = this.refs.loadMoreButton;
    this.#syncRenderedPagesFromDom();

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#setupLoadMoreButton();

    this.#backfillPreviousPages()
      .then(() => this.#restoreScrollTarget())
      .finally(() => {
        if (this.isConnected) this.#observeViewMore();
      });

    window.addEventListener('pageshow', this.#handlePageShow);

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    window.removeEventListener('pageshow', this.#handlePageShow);
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    this.#hasRecordedViewMorePreviousState = false;
    this.#viewMorePreviousIntersecting = false;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                if (!this.#hasRecordedViewMorePreviousState) {
                  this.#hasRecordedViewMorePreviousState = true;
                  this.#viewMorePreviousIntersecting = true;
                  continue;
                }

                const wasIntersecting = this.#viewMorePreviousIntersecting;
                this.#viewMorePreviousIntersecting = true;

                // Only load previous pages when the user scrolls back up to the top,
                // not on initial page load when ?page=N is already in the viewport.
                if (!wasIntersecting) {
                  this.#renderPreviousPage();
                }
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            } else if (entry.target === viewMorePrevious) {
              this.#hasRecordedViewMorePreviousState = true;
              this.#viewMorePreviousIntersecting = false;
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Don't observe while backfilling previous pages on direct ?page=N entry.
    if (this.#isBackfilling) return;

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * Sets up the load more button click handler
   */
  #setupLoadMoreButton() {

    if (!this.loadMoreButton) return;
    
    this.loadMoreButton.addEventListener('click', this.#handleLoadMore.bind(this));
  }

  /**
   * Handles the load more button click
   */
  async #handleLoadMore() {
    console.log('hi click')
    const { grid } = this.refs;

    if (!this.loadMoreButton || !grid) return;

    // Capture the button reference at click time so we always restore the
    // exact button the user clicked, even if `this.loadMoreButton` is
    // reassigned later in the success path.
    const button = this.loadMoreButton;

    button.classList.add('loading');
    if (button instanceof HTMLButtonElement) {
      button.disabled = true;
    }

    /** Restore the original button if it's still in the DOM (e.g. on early return / no replacement). */
    const restoreButton = () => {
      if (!button.isConnected) return;
      button.classList.remove('loading');
      if (button instanceof HTMLButtonElement) {
        button.disabled = false;
      }
    };

    try {
      const nextPage = this.#getPage('next');

      if (!nextPage || !this.#shouldUsePage(nextPage)) return;
      if (this.#renderedPages.has(nextPage.page)) return;

      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('next');

        await promise;
        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements) return;
      }

      const newItems = this.#filterDuplicateItems(grid, [...nextPageItemElements]);
      if (newItems.length === 0) {
        this.#renderedPages.add(nextPage.page);
        return;
      }

      // Append the new products in chunks to avoid blocking the main thread
      // when the page contains many product cards. Each chunk yields back to
      // the browser via rAF so layout/paint/connectedCallback work for the
      // previous batch can flush before the next batch is inserted.
      await this.#appendInChunks(grid, newItems);

      this.#renderedPages.add(nextPage.page);
      this.#aspectRatioHelper?.processNewElements();
      this.#updateProductCount();

      // Only advance the URL — never rewrite to a lower page number.
      const currentUrlPage = Number(new URL(window.location.href).searchParams.get('page')) || 1;
      if (nextPage.page > currentUrlPage) {
        history.pushState('', '', nextPage.url.toString());
      }

      // Get the new load more button from the fetched page (if it exists)
      const pageHTML = this.pages.get(nextPage.page);
      if (pageHTML) {
        const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
        const newLoadMoreContainer = parsedPage.querySelector('.load-more-container');

        // Remove the old button container
        const oldContainer = button.closest('.load-more-container');
        if (oldContainer) {
          if (newLoadMoreContainer) {
            // Replace with new button
            oldContainer.replaceWith(newLoadMoreContainer);

            this.loadMoreButton = newLoadMoreContainer.querySelector('.load-more-button');
            // Re-setup the event listener on the new button
            this.#setupLoadMoreButton();
          } else {
            // No more pages, remove the container
            oldContainer.remove();
          }
        }
      }

      // Prefetch the next page
      requestIdleCallback(() => {
        this.#fetchPage('next');
      });
    } catch (error) {
      console.error('Error loading more products:', error);
    } finally {
      // If the success path replaced the button container, the captured
      // `button` is no longer connected and `restoreButton` is a no-op.
      // Otherwise we always reset the loading/disabled state so the UI never
      // appears frozen, even on early returns.
      restoreButton();
    }
  }

  /**
   * Append nodes to a parent in small chunks, yielding to the browser between
   * chunks so we don't block the main thread when many custom elements need
   * to upgrade at once (each product card spawns several nested components).
   *
   * @param {Element} parent
   * @param {Element[]} nodes
   * @param {number} [chunkSize]
   */
  async #appendInChunks(parent, nodes, chunkSize = 6) {
    if (nodes.length <= chunkSize) {
      parent.append(...nodes);
      return;
    }

    for (let i = 0; i < nodes.length; i += chunkSize) {
      parent.append(...nodes.slice(i, i + chunkSize));
      // Yield to the browser between chunks. rAF is enough to let layout +
      // custom element connectedCallbacks for the previous chunk flush.
      if (i + chunkSize < nodes.length) {
        await new Promise((resolve) => requestAnimationFrame(() => resolve(undefined)));
      }
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, false, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    if (this.#isRenderingNext) return;

    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    if (this.#renderedPages.has(nextPage.page)) return;

    this.#isRenderingNext = true;

    try {
      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('next');

        await promise;
        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements) return;
      }

      const newItems = this.#filterDuplicateItems(grid, [...nextPageItemElements]);
      if (newItems.length === 0) {
        this.#renderedPages.add(nextPage.page);
        return;
      }

      grid.append(...newItems);

      this.#renderedPages.add(nextPage.page);

      this.#aspectRatioHelper?.processNewElements();
      this.#updateProductCount();

      // Only advance the URL — never rewrite to a lower page number.
      // nextPage.url already has hash stripped via url.hash = '' in #getPage.
      const currentUrlPage = Number(new URL(window.location.href).searchParams.get('page')) || 1;
      if (nextPage.page > currentUrlPage) {
        history.pushState('', '', nextPage.url.toString());
      }

      requestIdleCallback(() => {
        this.#fetchPage('next');
      });
    } finally {
      this.#isRenderingNext = false;
    }
  }

  /**
   * @param {Element} [scrollAnchor] - Element to keep in view when prepending (used during backfill).
   * @param {number} [pageOverride] - Explicit page number to render, bypassing the stale-ref lookup during backfill.
   */
  async #renderPreviousPage(scrollAnchor = undefined, pageOverride = undefined) {
    if (this.#isRenderingPrevious) return;

    const { grid, viewMorePrevious } = this.refs;

    if (!grid) return;

    let previousPage;
    if (pageOverride !== undefined) {
      const pageUrl = new URL(window.location.href);
      pageUrl.searchParams.set('page', pageOverride.toString());
      pageUrl.hash = '';
      previousPage = { page: pageOverride, url: pageUrl };
      if (!this.#shouldUsePage(previousPage)) return;
    } else {
      previousPage = this.#getPage('previous');
      if (!previousPage || !this.#shouldUsePage(previousPage)) {
        if (viewMorePrevious) viewMorePrevious.remove();
        return;
      }
    }

    if (this.#renderedPages.has(previousPage.page)) return;

    this.#isRenderingPrevious = true;

    // Show loading state
    if (viewMorePrevious) viewMorePrevious.setAttribute('aria-busy', 'true');

    try {
      let previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) {
        if (pageOverride !== undefined) {
          // Fetch the specific page directly — #fetchPage reads #getPage which
          // uses this.refs.cards, but refs may be stale during backfill because
          // the MutationObserver is registered lazily via requestIdleCallback.
          await this.#fetchSpecificPage(previousPage.page, previousPage.url);
        } else {
          const promise = new Promise((res) => {
            this.#resolvePreviousPagePromise = res;
          });

          // Trigger the fetch for this page
          this.#fetchPage('previous');

          await promise;
        }
        previousPageItemElements = this.#getGridForPage(previousPage.page);
        if (!previousPageItemElements) {
          return;
        }
      }

      const newItems = this.#filterDuplicateItems(grid, [...previousPageItemElements]);
      if (newItems.length === 0) {
        this.#renderedPages.add(previousPage.page);
        return;
      }

      const anchorElement = scrollAnchor ?? grid.firstElementChild;
      const scrollTop = window.scrollY;
      const oldHeight = anchorElement ? anchorElement.getBoundingClientRect().top + window.scrollY : 0;

      // Prepend the new elements
      grid.prepend(...newItems);

      this.#renderedPages.add(previousPage.page);

      this.#aspectRatioHelper?.processNewElements();
      this.#updateProductCount();

      // Calculate and adjust scroll position to maintain the same view
      if (anchorElement) {
        const newHeight = anchorElement.getBoundingClientRect().top + window.scrollY;
        const heightDiff = newHeight - oldHeight;
        window.scrollTo({
          top: scrollTop + heightDiff,
          behavior: 'instant',
        });
      }

      if (previousPage.page === 1) {
        await this.#ensurePromoTiles();
      }

      if (pageOverride === undefined) {
        requestIdleCallback(() => {
          this.#fetchPage('previous');
        });
      }
    } finally {
      if (viewMorePrevious) viewMorePrevious.removeAttribute('aria-busy');
      this.#isRenderingPrevious = false;
    }
  }

  /**
   * Returns the set of product IDs already present in the grid.
   *
   * @param {Element} grid
   * @returns {Set<string>}
   */
  #getExistingProductIds(grid) {
    return new Set(
      [...grid.querySelectorAll(':scope > [data-product-id]')].map(
        (card) => card.getAttribute('data-product-id') ?? ''
      )
    );
  }

  /**
   * Filters out product cards that are already in the grid to prevent duplicates.
   *
   * @param {Element} grid
   * @param {Element[]} items
   * @returns {Element[]}
   */
  #filterDuplicateItems(grid, items) {
    const existingIds = this.#getExistingProductIds(grid);

    return items.filter((item) => {
      const productId = item.getAttribute('data-product-id');
      return productId && !existingIds.has(productId);
    });
  }

  /**
   * Updates the visible product count text and progress bar
   * (e.g. "You've viewed X of Y products").
   */
  #updateProductCount() {
    const { grid, productCountCurrent, productCountTotal, productCountProgress, productCountProgressBar } = this.refs;
    if (!grid) return;

    const visibleCount = this.#getExistingProductIds(grid).size;

    if (productCountCurrent) {
      productCountCurrent.textContent = String(visibleCount);
    }

    const totalCount = Number(productCountTotal?.textContent) || visibleCount;
    const percent = totalCount > 0 ? Math.min(100, (visibleCount / totalCount) * 100) : 0;

    if (productCountProgressBar) {
      productCountProgressBar.style.width = `${percent}%`;
    }

    if (productCountProgress) {
      productCountProgress.setAttribute('aria-valuenow', String(visibleCount));
    }
  }

  #syncRenderedPagesFromDom() {
    this.#renderedPages.clear();

    const { cards } = this.refs;
    if (!Array.isArray(cards)) return;

    for (const card of cards) {
      const page = Number(card.dataset.page);
      if (!Number.isNaN(page)) {
        this.#renderedPages.add(page);
      }
    }
  }

  /**
   * Scrolls to the product card the customer clicked before leaving the PLP.
   * Uses sessionStorage because BFCache is unavailable when third-party chat websockets are open.
   */
  async #restoreScrollTarget() {
    const target = consumePlpScrollTarget();
    if (!target?.productId) return;

    const { grid } = this.refs;
    if (!grid) return;

    let card = grid.querySelector(`[data-product-id="${CSS.escape(target.productId)}"]`);

    if (!card && target.page) {
      await this.#ensurePageRendered(target.page);
      card = grid.querySelector(`[data-product-id="${CSS.escape(target.productId)}"]`);
    }

    if (!card) return;

    requestAnimationFrame(() => {
      card.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  /**
   * Ensures a paginated page is present in the grid (used when restoring scroll target).
   *
   * @param {number} page
   */
  async #ensurePageRendered(page) {
    if (this.#renderedPages.has(page)) return;

    const initialPage = this.#getInitialPageFromUrl();

    if (page < initialPage) {
      await this.#renderPreviousPage(undefined, page);
      return;
    }

    const lastPage = Number(this.refs.grid?.dataset.lastPage) || page;

    while (!this.#renderedPages.has(page) && Math.max(...this.#renderedPages) < page) {
      const highestPage = Math.max(...this.#renderedPages);
      if (highestPage >= lastPage) break;

      await this.#renderNextPage();

      if (!this.#renderedPages.has(highestPage + 1)) break;
    }
  }

  /**
   * Reads the page query param from the current URL.
   *
   * @returns {number}
   */
  #getInitialPageFromUrl() {
    const page = Number(new URL(window.location.href).searchParams.get('page'));
    return Number.isNaN(page) || page < 1 ? 1 : page;
  }

  /**
   * When landing directly on ?page=N, prepend earlier pages (1…N-1) so the
   * grid is complete without changing the browser URL.
   */
  async #backfillPreviousPages() {
    const initialPage = this.#getInitialPageFromUrl();
    if (initialPage <= 1) return;

    const { cards } = this.refs;
    if (!Array.isArray(cards) || cards.length === 0) return;

    const renderedPages = [...this.#renderedPages];
    if (renderedPages.length === 0) return;

    const lowestRenderedPage = Math.min(...renderedPages);

    // Skip when earlier pages are already present (e.g. BFCache or load-more history).
    if (lowestRenderedPage < initialPage) return;

    const url = new URL(window.location.href);
    const hashTarget = url.hash ? document.getElementById(url.hash.slice(1)) : null;
    const scrollAnchor =
      hashTarget ??
      cards.find((card) => Number(card.dataset.page) === initialPage) ??
      cards[0];

    this.#isBackfilling = true;
    this.infinityScrollObserver?.disconnect();

    try {
      // Iterate explicitly from initialPage-1 down to 1 using pageOverride to
      // bypass #getPage, which reads this.refs.cards. The MutationObserver that
      // keeps refs up-to-date is registered lazily via requestIdleCallback and
      // may not have fired yet, so cards[0] stays stale after each prepend —
      // causing the old while-loop to stop after one page.
      for (let pageNum = initialPage - 1; pageNum >= 1; pageNum--) {
        if (this.#renderedPages.has(pageNum)) continue;
        await this.#renderPreviousPage(scrollAnchor, pageNum);
      }

      this.#updateProductCount();
      await this.#ensurePromoTiles();

      // After backfilling, strip any hash from the URL while keeping the page
      // number at its current (highest) value — not rewriting to a lower page.
      const finalUrl = new URL(window.location.href);
      if (finalUrl.hash) {
        finalUrl.hash = '';
        history.replaceState('', '', finalUrl.toString());
      }
    } finally {
      this.#isBackfilling = false;
    }
  }

  /**
   * Inserts page-1 promotional tiles when missing (e.g. after backfilling ?page=N).
   * Tiles are positioned via CSS order and only exist in the page-1 section HTML.
   */
  async #ensurePromoTiles() {
    const { grid } = this.refs;
    if (!grid || grid.querySelector(':scope > .product-grid__item-banner')) return;

    if (!this.pages.has(1)) {
      const pageOneUrl = new URL(window.location.href);
      pageOneUrl.searchParams.set('page', '1');
      pageOneUrl.hash = '';
      await this.#fetchSpecificPage(1, pageOneUrl);
    }

    const promoTiles = this.#getPromoTilesForPage(1);
    if (!promoTiles?.length) return;

    grid.append(...promoTiles);
    this.#aspectRatioHelper?.processNewElements();
  }

  /**
   * @param {number} page
   * @returns {Element[] | undefined}
   */
  #getPromoTilesForPage(page) {
    if (page !== 1) return;

    const pageHTML = this.pages.get(page);
    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;

    return [...gridElement.querySelectorAll(':scope > .product-grid__item-banner')];
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();
    this.#renderedPages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#syncRenderedPagesFromDom();
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
